import React, { useRef, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ImagePlus, Film, Trash2, Loader2, EyeOff } from "lucide-react";
import { userIsPremium } from "@/components/ProviderPlan";
import { userPlan } from "@/lib/plans";

const LIMITS = { photos: { max: 25, accept: "image/jpeg,image/png,image/webp", label: "Foto (JPG, PNG, WEBP · max 25 MB, fino a 5)" },
  videos: { max: 200, accept: "video/mp4,video/quicktime,video/webm", label: "Video (MP4, MOV, WEBM · max 200 MB, fino a 5)" } };

function UploadBox({ kind, onDone }) {
  const ref = useRef(null);
  const [progress, setProgress] = useState(null);
  const cfg = LIMITS[kind];

  const upload = async (file) => {
    if (file.size > cfg.max * 1024 * 1024) { toast.error(`File troppo grande (max ${cfg.max} MB)`); return; }
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      await api.post("/me/media", fd, { onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || 1)) * 100)) });
      toast.success(kind === "photos" ? "Foto caricata" : "Video caricato");
      onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Caricamento non riuscito");
    } finally { setProgress(null); if (ref.current) ref.current.value = ""; }
  };

  return (
    <button type="button" data-testid={`media-upload-${kind}`} disabled={progress !== null} onClick={() => ref.current?.click()}
      className="aspect-video rounded-xl border-2 border-dashed border-gold/50 hover:border-gold bg-gold/5 flex flex-col items-center justify-center gap-2 text-navy transition-colors">
      <input ref={ref} type="file" accept={cfg.accept} className="hidden" data-testid={`media-input-${kind}`} onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
      {progress !== null ? (<><Loader2 className="w-6 h-6 animate-spin text-gold" /><span className="text-sm font-semibold">{progress}%</span></>)
        : (<>{kind === "photos" ? <ImagePlus className="w-6 h-6 text-gold" /> : <Film className="w-6 h-6 text-gold" />}<span className="text-xs font-semibold">Aggiungi {kind === "photos" ? "foto" : "video"}</span></>)}
    </button>
  );
}

export function MediaManager() {
  const { user, refresh } = useAuth();
  const media = user?.media || { photos: [], videos: [] };
  const premium = userIsPremium(user);

  const remove = async (url) => {
    await api.post("/me/media/remove", { url });
    toast.success("Rimosso");
    refresh();
  };

  const section = (kind) => (
    <div>
      <div className="overline text-slate-500 mb-3">{LIMITS[kind].label}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {(media[kind] || []).map((u, i) => (
          <div key={u} className="relative group aspect-video rounded-xl overflow-hidden bg-navy" data-testid={`media-item-${kind}-${i}`}>
            {kind === "photos" ? <img src={mediaUrl(u)} alt="" className="w-full h-full object-cover" />
              : <video src={mediaUrl(u)} className="w-full h-full object-cover" muted preload="metadata" />}
            <button type="button" onClick={() => remove(u)} data-testid={`media-remove-${kind}-${i}`} aria-label="Rimuovi"
              className="absolute top-2 right-2 p-1.5 rounded-full bg-white/90 text-red-600 opacity-90 hover:opacity-100"><Trash2 className="w-4 h-4" /></button>
            {kind === "videos" && i >= ({ free: 1, standard: 2, professional: 5 })[userPlan(user)] && <div className="absolute inset-0 bg-navy/70 flex items-center justify-center gap-1 text-white text-xs font-semibold pointer-events-none" data-testid={`media-hidden-${i}`}><EyeOff className="w-4 h-4"/>Oscurato</div>}
          </div>
        ))}
        {(media[kind] || []).length < 5 && <UploadBox kind={kind} onDone={refresh} />}
      </div>
    </div>
  );

  return (
    <div className="mt-8 card-premium p-6 space-y-6" data-testid="media-manager">
      <div>
        <h3 className="font-display text-xl font-bold">Foto e video del profilo</h3>
        <p className="text-sm text-slate-600 mt-1">Carica materiale in alta qualità: i file vengono conservati alla risoluzione originale e mostrati nel tuo profilo pubblico.</p>
        {!premium && <p className="text-sm text-amber-700 mt-2" data-testid="media-free-note">Piano Gratuito: i committenti vedono solo 3 foto scelte a caso e 1 video. Con Standard vedono fino a 5 foto e 2 video, con Professional fino a 5 foto e 5 video.</p>}
      </div>
      {section("photos")}
      {section("videos")}
    </div>
  );
}
